import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';

import { useConsent } from '../../../context/ConsentContext';
import * as styles from './CookieBanner.module.css';

/**
 * Bannière de consentement cookies.
 * - Vue simple : Accepter tout / Refuser tout / Personnaliser.
 * - Vue détaillée : cases par catégorie (nécessaires, mesure d'audience, marketing).
 *
 * Réouverture depuis n'importe où via :
 *   window.dispatchEvent(new Event('cbjm:openCookiePreferences'))
 */
const CookieBanner = () => {
  const {
    showBanner,
    consent,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
  } = useConsent();

  const [detailed, setDetailed] = useState(false);
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);

  // Permet de rouvrir la bannière depuis un lien du footer ou de la page légale.
  useEffect(() => {
    const handler = () => {
      setDetailed(true);
      openPreferences();
    };
    window.addEventListener('cbjm:openCookiePreferences', handler);
    return () =>
      window.removeEventListener('cbjm:openCookiePreferences', handler);
  }, [openPreferences]);

  // Resynchronise les cases avec le choix mémorisé à l'ouverture.
  useEffect(() => {
    if (showBanner) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [showBanner, consent.analytics, consent.marketing]);

  if (!showBanner) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Gestion des cookies"
    >
      <div className={styles.panel}>
        {!detailed ? (
          <>
            <div className={styles.text}>
              <h2 className={styles.title}>Nous respectons votre vie privée</h2>
              <p className={styles.body}>
                Nous utilisons des cookies pour assurer le bon fonctionnement du
                site et, avec votre accord, pour mesurer l'audience et améliorer
                votre expérience. Vous pouvez accepter, refuser ou personnaliser
                vos choix. Pour en savoir plus, consultez notre{' '}
                <Link to="/politique-confidentialite" className={styles.inlineLink}>
                  politique de confidentialité
                </Link>
                .
              </p>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => setDetailed(true)}
              >
                Personnaliser
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={rejectAll}
              >
                Tout refuser
              </button>
              <button
                type="button"
                className={styles.primary}
                onClick={acceptAll}
              >
                Tout accepter
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.text}>
              <h2 className={styles.title}>Personnaliser les cookies</h2>
              <p className={styles.body}>
                Activez ou désactivez chaque catégorie. Les cookies nécessaires
                ne peuvent pas être désactivés car ils sont indispensables au
                fonctionnement du site.
              </p>
            </div>

            <ul className={styles.categories}>
              <li className={styles.category}>
                <div className={styles.categoryHead}>
                  <span className={styles.categoryName}>
                    Cookies nécessaires
                  </span>
                  <span className={styles.always}>Toujours actifs</span>
                </div>
                <p className={styles.categoryDesc}>
                  Indispensables : navigation, panier, connexion, paiement
                  sécurisé, mémorisation de vos préférences de cookies.
                </p>
              </li>

              <li className={styles.category}>
                <div className={styles.categoryHead}>
                  <label className={styles.categoryName} htmlFor="consent-analytics">
                    Mesure d'audience
                  </label>
                  <label className={styles.toggle}>
                    <input
                      id="consent-analytics"
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                    />
                    <span className={styles.slider} />
                  </label>
                </div>
                <p className={styles.categoryDesc}>
                  Statistiques de visite et comportement anonymisé (Google
                  Analytics 4, Microsoft Clarity) pour améliorer le site.
                </p>
              </li>

              <li className={styles.category}>
                <div className={styles.categoryHead}>
                  <label className={styles.categoryName} htmlFor="consent-marketing">
                    Marketing
                  </label>
                  <label className={styles.toggle}>
                    <input
                      id="consent-marketing"
                      type="checkbox"
                      checked={marketing}
                      onChange={(e) => setMarketing(e.target.checked)}
                    />
                    <span className={styles.slider} />
                  </label>
                </div>
                <p className={styles.categoryDesc}>
                  Cookies publicitaires et de remarketing pour vous proposer des
                  contenus pertinents.
                </p>
              </li>
            </ul>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={rejectAll}
              >
                Tout refuser
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => savePreferences({ analytics, marketing })}
              >
                Enregistrer mes choix
              </button>
              <button
                type="button"
                className={styles.primary}
                onClick={acceptAll}
              >
                Tout accepter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
